import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { childAgeYearsFromBirthDate } from '../../../../shared/helpers/child-age.helper';
import { ParentAiChildDropdownComponent } from '../../../../features/parent-ai-assistant/components/parent-ai-child-dropdown/parent-ai-child-dropdown.component';
import { childAvatarFallbackLabel } from '../../../../features/parent-ai-assistant/helpers/child-avatar-fallback';
import type { ChildOption } from '../../../../features/parent-ai-assistant/models/child-option.model';
import { ParentAiService } from '../../../../features/parent-ai-assistant/parent-ai.service';
import { ParentFamilyContextService } from '../../../../features/parent-family/parent-family-context.service';
import { FamilyKidsService } from '../../../../features/parent-family/services/family-kids.service';
import { UserService } from '../../../../features/user/service/user.service';

@Component({
  selector: 'app-parent-family-context-bar',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ParentAiChildDropdownComponent],
  templateUrl: './parent-family-context-bar.component.html',
  styleUrl: './parent-family-context-bar.component.scss',
})
export class ParentFamilyContextBarComponent {
  #family = inject(ParentFamilyContextService);
  #familyKids = inject(FamilyKidsService);
  #parentAi = inject(ParentAiService);
  #userService = inject(UserService);
  #translate = inject(TranslateService);

  lockKidScope = input(false);

  showChildSelector = signal(false);

  selectedChildId = this.#family.selectedChildId;

  user = computed(() => this.#userService.user());
  children = computed<ChildOption[]>(() =>
    this.#familyKids.toChildOptions(
      this.#translate.instant('app.parentPatrol.labels.childFallback'),
    ),
  );

  selectedChild = computed(() => {
    const id = this.selectedChildId();
    if (!id) {
      return null;
    }
    return this.children().find((c) => c.id === id) ?? null;
  });

  isNeutralScope = computed(() => this.#parentAi.isNeutralParentingScope());

  constructor() {
    effect(() => {
      if (this.lockKidScope()) {
        this.#parentAi.selectNeutralChild();
        this.showChildSelector.set(false);
      }
    });
    effect(() => {
      this.#parentAi.isVerificationMode.set(this.isVerificationMode());
    });
    effect(() => {
      this.#parentAi.syncInitialChildSelection(this.children());
    });
  }

  isVerificationMode = computed(() => {
    if (this.user() == null) {
      return false;
    }
    return !this.#familyKids.hasChildren();
  });

  @HostListener('document:click')
  closeChildSelector(): void {
    this.showChildSelector.set(false);
  }

  toggleChildSelector(event: MouseEvent): void {
    event.stopPropagation();
    if (this.lockKidScope()) {
      return;
    }
    this.showChildSelector.update((open) => !open);
  }

  avatarFallback(child: ChildOption): string {
    return childAvatarFallbackLabel(child);
  }

  childContextLabel(child: ChildOption): string {
    const raw = this.#familyKids.childById(child.id);
    const ageYears = childAgeYearsFromBirthDate(raw?.birthDate);
    if (ageYears == null) {
      return child.name;
    }
    return this.#translate.instant('app.parentFamily.childWithAge', {
      name: child.name,
      age: ageYears,
    });
  }

  selectChild(child: ChildOption): void {
    if (this.lockKidScope()) {
      return;
    }
    this.#parentAi.selectLinkedChild(child.id);
    this.showChildSelector.set(false);
  }

  selectNeutral(): void {
    this.#parentAi.selectNeutralChild();
    this.showChildSelector.set(false);
  }
}
